import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, Leaf, ArrowDown, ArrowUp, Gift, X, Certificate, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TreeTokenWidget() {
  const [data, setData] = useState(null);
  const [showRedeem, setShowRedeem] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const fetchTokens = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/tokens`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    const t = setInterval(fetchTokens, 30000);
    return () => clearInterval(t);
  }, [fetchTokens]);

  if (!data) return null;
  const { wallet, history, config } = data;
  const balance = wallet?.balance || 0;
  const lifetime = wallet?.lifetime_earned || 0;
  const tonsAvailable = balance / (config?.redeem_tokens_per_ton || 100);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-emerald-900 to-stone-900 border border-emerald-700/30 rounded-2xl p-5 text-white"
        data-testid="tree-token-widget"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center">
            <Coins size={20} weight="fill" className="text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">TREE Tokens</p>
            <p className="font-heading text-3xl font-light text-white" data-testid="tree-token-balance">
              {balance.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4 text-[11px]">
          <div className="bg-white/5 rounded-lg p-2.5">
            <p className="text-emerald-300/60 uppercase tracking-wider text-[9px]">Lifetime earned</p>
            <p className="font-medium text-emerald-100">{lifetime.toLocaleString()}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2.5">
            <p className="text-emerald-300/60 uppercase tracking-wider text-[9px]">Offset capacity</p>
            <p className="font-medium text-emerald-100" data-testid="tree-token-tons">
              {tonsAvailable.toFixed(2)} tCO₂e
            </p>
          </div>
        </div>
        <p className="text-[10px] text-emerald-200/70 mb-3">
          Earn <strong>{config?.earn_per_tree} TREE</strong> per tree planted ·
          Redeem <strong>{config?.redeem_tokens_per_ton} TREE</strong> = 1 tCO₂e free certificate
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowRedeem(true)}
            disabled={balance < (config?.redeem_tokens_per_ton || 100)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-stone-700 disabled:text-stone-400 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
            data-testid="open-token-redeem"
          >
            <Gift size={14} weight="fill" /> Redeem for Certificate
          </button>
          <button
            onClick={() => setShowHistory(true)}
            className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-2 rounded-lg"
            data-testid="open-token-history"
          >
            History
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {showRedeem && (
          <RedeemModal
            wallet={wallet}
            config={config}
            onClose={() => setShowRedeem(false)}
            onSuccess={() => { fetchTokens(); }}
          />
        )}
        {showHistory && (
          <HistoryModal history={history} onClose={() => setShowHistory(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

function RedeemModal({ wallet, config, onClose, onSuccess }) {
  const tokensPerTon = config?.redeem_tokens_per_ton || 100;
  const maxTons = Math.floor((wallet?.balance || 0) / tokensPerTon * 100) / 100;
  const [tons, setTons] = useState(Math.min(1, maxTons));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const tokensNeeded = Math.round(tons * tokensPerTon);

  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/me/tokens/redeem`, { tons }, { withCredentials: true });
      setResult(res.data);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || "Redemption failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="token-redeem-modal"
    >
      <motion.div
        initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
        className="bg-white rounded-3xl max-w-md w-full overflow-hidden"
      >
        <header className="flex items-center gap-3 p-5 border-b border-stone-200">
          <Gift size={20} weight="duotone" className="text-emerald-700" />
          <h2 className="font-heading text-lg font-semibold text-stone-900 flex-1">Redeem for Certificate</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="token-redeem-close">
            <X size={18} weight="bold" />
          </button>
        </header>

        {result ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Certificate size={28} weight="duotone" className="text-emerald-700" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-stone-900 mb-1">Certificate Issued</h3>
            <p className="text-xs text-stone-500 mb-4">Serial: <code className="text-stone-700">{result.certificate.serial_number}</code></p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${result.certificate.cert_id}/download`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-5 py-2.5 rounded-full"
              data-testid="token-redeem-download"
            >
              <DownloadSimple size={14} weight="bold" /> Download Certificate
            </a>
            <button onClick={onClose} className="block mx-auto mt-3 text-xs text-stone-500 hover:text-stone-900">
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Your balance</p>
              <p className="font-heading text-2xl font-light text-stone-900">{(wallet?.balance || 0).toLocaleString()} <span className="text-sm text-stone-400">TREE</span></p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-400 block mb-2">
                How many tCO₂e to offset?
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range" min="0.1" max={Math.max(maxTons, 0.1)} step="0.1"
                  value={tons} onChange={(e) => setTons(Number(e.target.value))}
                  className="flex-1 accent-emerald-700"
                  data-testid="token-redeem-range"
                />
                <input
                  type="number" min="0.1" max={maxTons} step="0.1"
                  value={tons} onChange={(e) => setTons(Math.min(maxTons, Math.max(0.1, Number(e.target.value) || 0.1)))}
                  className="w-20 px-2 py-1.5 border border-stone-200 rounded-lg text-sm text-right focus:outline-none focus:border-emerald-500"
                  data-testid="token-redeem-tons"
                />
                <span className="text-xs text-stone-500">tCO₂e</span>
              </div>
              <p className="text-[10px] text-stone-400 mt-1">Max: {maxTons.toFixed(2)} tCO₂e (based on your balance)</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
              You&apos;ll burn <strong>{tokensNeeded.toLocaleString()} TREE</strong> tokens for a <strong>{tons.toFixed(2)} tCO₂e</strong> retirement certificate.
            </div>
            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}
            <button
              onClick={submit}
              disabled={submitting || tokensNeeded > (wallet?.balance || 0) || tons <= 0}
              className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-300 text-white text-sm font-medium py-3 rounded-xl flex items-center justify-center gap-2"
              data-testid="token-redeem-submit"
            >
              {submitting ? "Issuing..." : <><Gift size={14} weight="fill" /> Redeem & Issue Certificate</>}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function HistoryModal({ history, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="token-history-modal"
    >
      <motion.div
        initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
        className="bg-white rounded-3xl max-w-md w-full overflow-hidden max-h-[85vh] flex flex-col"
      >
        <header className="flex items-center gap-3 p-5 border-b border-stone-200">
          <Leaf size={20} weight="duotone" className="text-emerald-700" />
          <h2 className="font-heading text-lg font-semibold text-stone-900 flex-1">Token History</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="token-history-close">
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="overflow-y-auto p-4 space-y-2">
          {(!history || history.length === 0) ? (
            <p className="text-center text-sm text-stone-400 py-8">No transactions yet. Plant a tree to start earning!</p>
          ) : history.map((h) => {
            const isEarn = h.type === "earn";
            return (
              <div key={h.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`token-history-${h.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isEarn ? "bg-emerald-100" : "bg-rose-100"}`}>
                  {isEarn ? <ArrowUp size={14} weight="bold" className="text-emerald-700" />
                          : <ArrowDown size={14} weight="bold" className="text-rose-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-stone-900 truncate">{h.reason}</p>
                  <p className="text-[10px] text-stone-400">{new Date(h.at).toLocaleString()}</p>
                </div>
                <p className={`text-sm font-medium ${isEarn ? "text-emerald-700" : "text-rose-700"}`}>
                  {isEarn ? "+" : ""}{h.amount}
                </p>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}
